import React, { useEffect, useState } from 'react';
import { api, AED } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';

const STEP_LABEL = { project_manager: 'PM Review', md: 'MD Approval', payments: 'Payments' };
const STATUS_CLASS = { pending_pm: 'status-warning', pending_md: 'status-warning', pending_payments: 'status-info', paid: 'status-success', rejected: 'status-danger' };

export default function Approvals() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(null);
  const [vendors, setVendors] = useState({});
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(null);
  const [viewInv, setViewInv] = useState(null);

  const load = async () => {
    const [ib, ap, vd] = await Promise.all([
      api.get('/invoices/inbox/me'),
      api.get('/invoices'),
      api.get('/vendors'),
    ]);
    setInbox(ib.data);
    setAll(ap.data);
    setVendors(Object.fromEntries(vd.data.map(v => [v.id, v])));
  };
  useEffect(() => { load(); }, []);

  const act = async (invId, action) => {
    setBusy(invId);
    try {
      await api.post(`/invoices/${invId}/approve`, { action, note });
      setNote(''); setSelected(null);
      await load();
    } finally { setBusy(null); }
  };

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="approvals-page">
      <div>
        <div className="overline">Workflow</div>
        <h1 className="font-serif text-4xl">Approvals</h1>
        <p className="text-slate-600 mt-2">Accountant → Project Manager → Managing Director → Payments</p>
      </div>

      <div className="glass p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16}/> <div className="font-serif text-2xl">Your inbox</div>
          <span className="status-pill status-warning">{inbox.length}</span>
        </div>
        {inbox.length === 0 && <div className="text-slate-400 text-sm py-6">Nothing waiting on you right now.</div>}
        <div className="space-y-3">
          {inbox.map(inv => (
            <div key={inv.id} className="p-4 bg-white/70 border border-white/70 rounded-2xl" data-testid={`inbox-inv-${inv.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 cursor-pointer" onClick={() => setViewInv(inv.id)}>
                  <div className="font-medium text-sm hover:underline">{inv.description}</div>
                  <div className="text-xs text-slate-500 mt-1">{inv.invoice_number} · Vendor: {vendors[inv.vendor_id]?.company || '—'}</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-xl">{AED(inv.amount_aed)}</div>
                  <span className={`status-pill mt-1 ${STATUS_CLASS[inv.status] || 'status-neutral'}`}>{STEP_LABEL[inv.current_step] || inv.status}</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600">
                Trail: {inv.approvals?.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 mr-2">
                    {a.action === 'approve' ? '✓' : a.action === 'reject' ? '✗' : '↑'} <span className="capitalize">{a.step?.replace('_',' ')}</span>
                  </span>
                ))}
              </div>
              {selected === inv.id ? (
                <div className="mt-3 flex gap-2 items-center">
                  <input value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Note (optional)"
                         data-testid={`approve-note-${inv.id}`}
                         className="flex-1 px-3 py-2 rounded-full border border-slate-200 bg-white/80 text-sm"/>
                  <button disabled={busy===inv.id} onClick={()=>act(inv.id, 'approve')} data-testid={`approve-confirm-${inv.id}`}
                          className="btn-primary text-xs flex items-center gap-1">
                    {busy===inv.id ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle size={12}/>} Approve
                  </button>
                  <button disabled={busy===inv.id} onClick={()=>act(inv.id, 'reject')}
                          className="px-4 py-2 rounded-full border border-red-200 bg-white text-red-600 text-xs hover:bg-red-50">
                    <XCircle size={12} className="inline mr-1"/> Reject
                  </button>
                  <button onClick={()=>{setSelected(null); setNote('');}} className="text-xs text-slate-500">Cancel</button>
                </div>
              ) : (
                <button onClick={()=>setSelected(inv.id)} data-testid={`review-inv-${inv.id}`}
                        className="mt-3 btn-primary text-xs">Review & act</button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="glass p-6">
        <div className="font-serif text-2xl mb-4">All invoices</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left border-b border-slate-200/60">
              <th className="py-2">Invoice</th><th>Vendor</th><th>Description</th><th className="text-right">Amount</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {all.map(inv => (
              <tr key={inv.id} className="border-b border-slate-100/70">
                <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
                <td>{vendors[inv.vendor_id]?.company || '—'}</td>
                <td className="max-w-xs truncate">{inv.description}</td>
                <td className="text-right">{AED(inv.amount_aed)}</td>
                <td><span className={`status-pill ${STATUS_CLASS[inv.status] || 'status-neutral'}`}>{inv.status?.replace(/_/g,' ')}</span></td>
              </tr>
            ))}
            {all.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>
      {viewInv && <InvoiceModal invoiceId={viewInv} onClose={() => setViewInv(null)} onUpdate={load}/>}
    </div>
  );
}
